import {useForm} from '@tanstack/react-form';
import {ValidationError} from 'yup';
import React from 'react';
import {Button} from '~/components/Button';
import {EditorField, FormColumn, FormControls, FormRow, FormTitle, InputField, IntentText} from '~/components/Form';
import formStyle from '~/components/Form/index.module.scss';
import {IModalComponent, IModalComponentClose} from '~/components/Modal';
import {Intent} from '~/lib/intent';
import {prettyJSON} from '~/lib/json';
import {BidderFormCtx, Field, setNestedValue} from '~/modules/Bidders/forms/BidderFormBridge';
import {defaultPreTargetingVariable, IPreTargetingVariable, PreTargetingVariableSchema} from '~/modules/Bidders/models/PreTargeting';

export interface IPreTargetingVariableFormActions {
    setSubmitting: (submitting: boolean) => void;
    setStatus: (nextStatus: string | undefined) => void;
    setErrors: (errors: Record<string, any>) => void;
}

export interface IPreTargetingVariableForm {
    preTargetingVariable?: IPreTargetingVariable;
    create?: boolean;
    onSubmit?: (values: IPreTargetingVariable, formActions: IPreTargetingVariableFormActions, close: IModalComponentClose) => void | Promise<void>;
}

const FormShell = (props: React.FormHTMLAttributes<HTMLFormElement>) => {
    const classes = [formStyle.form, props.className].filter(Boolean);
    const childrenArray = React.Children.toArray(props.children);
    const titles: React.ReactNode[] = [];
    const content: React.ReactNode[] = [];
    const controls: React.ReactNode[] = [];

    childrenArray.forEach(child => {
        if (!React.isValidElement(child)) {
            content.push(child);
            return;
        }
        if (child.type === FormControls) {
            controls.push(child);
        } else if (child.type === FormTitle) {
            titles.push(child);
        } else {
            content.push(child);
        }
    });

    return <form {...props} className={classes.join(' ')}>
        {titles}
        <div className={formStyle.formBody}>
            {content}
        </div>
        {controls}
    </form>;
};

const getInitialValues = (value?: IPreTargetingVariable): IPreTargetingVariable => ({
    ...(value ?? defaultPreTargetingVariable),
    value: prettyJSON(value?.value ?? defaultPreTargetingVariable.value)
});

export const PreTargetingVariableForm: IModalComponent<IPreTargetingVariableForm> = props => {
    const closeModal = props.close ?? (() => undefined);
    const [status, setStatus] = React.useState<string>();
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, any>>({});

    const form = useForm({
        defaultValues: getInitialValues(props.preTargetingVariable),
        validators: {
            onSubmit: ({value}) => {
                try {
                    PreTargetingVariableSchema.validateSync(value, {abortEarly: false});
                    setFieldErrors({});
                    return undefined;
                } catch (error) {
                    if (!(error instanceof ValidationError)) {
                        return 'Validation failed';
                    }
                    const newErrors: Record<string, any> = {};
                    const issues = error.inner.length > 0 ? error.inner : [error];
                    issues.forEach(issue => {
                        if (!issue.path) return;
                        setNestedValue(newErrors, issue.path, issue.message);
                    });
                    setFieldErrors(newErrors);
                    return 'Validation failed';
                }
            }
        },
        onSubmit: async ({value}) => {
            if (!props.onSubmit) return;
            const formActions: IPreTargetingVariableFormActions = {
                setSubmitting: (_submitting: boolean) => undefined,
                setStatus,
                setErrors: (errors: Record<string, any>) => setFieldErrors(errors ?? {})
            };
            await Promise.resolve(props.onSubmit(value, formActions, closeModal));
        }
    });

    return <form.Subscribe selector={state => state}>
        {state => {
            const setFieldValue = (fieldName: string, fieldValue: unknown) => {
                setStatus(undefined);
                form.setFieldValue(fieldName as never, fieldValue as never);
            };

            const formBridge = {
                errors: fieldErrors,
                touched: {},
                submitCount: state.submissionAttempts,
                setFieldValue,
                setStatus,
                setSubmitting: (_submitting: boolean) => undefined,
            };

            const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
                event?.preventDefault();
                void form.handleSubmit();
            };

            const isSubmitting = state.isSubmitting;

            return <BidderFormCtx.Provider value={{form, formBridge}}>
                <FormShell onSubmit={handleSubmit}>
                    <FormTitle>{props.create ? 'Create variable' : 'Edit variable'}</FormTitle>
                    {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}
                    <FormRow caption='Name'>
                        <FormColumn>
                            <Field name='name'
                                options={{
                                    disabled: !props.create || isSubmitting
                                }}
                                component={InputField}
                            />
                        </FormColumn>
                    </FormRow>
                    <FormRow caption='Value'>
                        <FormColumn>
                            <Field name='value'
                                options={{
                                    disabled: isSubmitting,
                                    mode: 'json',
                                    canFullScreen: true
                                }}
                                component={EditorField}
                            />
                        </FormColumn>
                    </FormRow>
                    <FormControls>
                        <Button disabled={isSubmitting}
                            onClick={closeModal}
                        >Cancel</Button>
                        <Button type='submit'
                            disabled={isSubmitting}
                            intent={Intent.PRIMARY}
                            rightIcon={isSubmitting && <i className='fa fa-spinner fa-spin'/>}
                        >Save</Button>
                    </FormControls>
                </FormShell>
            </BidderFormCtx.Provider>;
        }}
    </form.Subscribe>;
};
